import React, { useRef, useLayoutEffect } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ScrollHintArrow from '../ScrollHintArrow';

gsap.registerPlugin(ScrollTrigger);

const Section = styled.section`
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background-image: url('https://assets.volkswagen.com/is/image/volkswagenag/red-background?Zml0PWNyb3AlMkMxJndpZD0xNzYwJmhlaT04ODAmZm10PWpwZWcmcWx0PTc5JmJmYz1vZmYmYzEwMA==');
  background-size: cover;
`;

const LogoContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100vh;
  width: 35vw;
  margin-left: 5vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  @media screen and (max-width: 1050px) {
    top: 10vh;
    width: 60vw;
    margin-left: 20vw;
    height: 40vh;
  }

  @media screen and (max-width: 860px) {
    width: 80vw;
    margin-left: 10vw;
  }
`;

const Logo = styled.svg`
  width: 100%;
`;

const CarCoverContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 100vh;
  width: 60vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  @media screen and (max-width: 1050px) {
    width: 80vw;
    margin-right: 10vw;
    height: 60vh;
    top: 40vh;
  }

  @media screen and (max-width: 860px) {
    width: 90vw;
    margin-right: 5vw;
  }
`;

const CarCover = styled.img`
  width: 100%;
`;

const Car = styled(CarCover)`
  position: absolute;
  opacity: 0;
`;

const FinalVideo = styled.video`
  position: absolute;
  bottom: 0;
  left: 40%;
  width: 20vw;
  height: auto;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  opacity: 1;
`;

export default function GTI50Reveal() {
  const container = useRef(null);
  const carCoverRef = useRef(null);
  const carRef = useRef(null);
  const finalVideoRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          id: 'gti50-reveal-pin',
          trigger: container.current,
          start: 'top top',
          end: '+=300%',
          scrub: 1, // scrub suave y continuo
          pin: true,
          anticipatePin: 1,
          // “Snap” magnético SOLO a partir del inicio de Fase 4:
          snap: {
            snapTo: (value) => {
              // calcula el progreso (0..1) donde arranca Fase 4
              const p4 = tl.labels?.p4Start ? tl.labels.p4Start / tl.duration() : 0.8;
              // margen para no “tironear” justo al cruzar la frontera
              const deadband = 0.01;

              // Antes de Fase 4: no forzamos nada, devuelve el valor tal cual
              if (value < p4 - deadband) return value;

              // Dentro de Fase 4: encaja al inicio o al final
              const halfway = p4 + (1 - p4) * 0.05;
              return value < halfway ? p4 : 1;
            },
            duration: 0.4, // velocidad del encaje
            ease: 'power2.out',
            delay: 0,
          },
        },
      });

      // --- FASES ---
      // Fase 1: coche tapado se desvanece
      tl.to(carCoverRef.current, { opacity: 0, scale: 0.95, duration: 1 });

      // Fase 2: coche destapado aparece con leve escala
      tl.fromTo(carRef.current, { opacity: 0, scale: 1.05 }, { opacity: 1, scale: 1, duration: 1 }, '-=0.8');

      // Fase 3: video entra desde abajo
      tl.fromTo(finalVideoRef.current, { y: '100%', scale: 1, opacity: 1 }, { y: '0%', duration: 1, ease: 'none' });

      // Marca el inicio de Fase 4 para que el snap sepa dónde encajar
      tl.addLabel('p4Start');

      // Fase 4: video se expande a pantalla completa
      tl.to(finalVideoRef.current, {
        left: '0%',
        width: '100vw',
        height: '100vh',
        duration: 1,
        ease: 'power2.out',
      });

      // IMPORTANTE: refrescar tras definir labels para que snap tenga todo
      tl.scrollTrigger?.refresh();
    }, container);

    return () => ctx.revert();
  }, []);

  return (
    <Section ref={container}>
      <LogoContainer>
        <Logo width="537" height="357" viewBox="0 0 537 357" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M39.5655 82.1136H68.6004V85.3064C68.6004 100.14 66.3887 110.816 61.9653 117.335C57.1428 124.452 50.0088 128.011 40.5633 128.011C31.2841 128.011 24.0004 124.519 18.7123 117.534C13.4574 110.483 10.83 100.805 10.83 88.4993C10.83 76.0272 13.474 66.1993 18.7622 59.0154C24.0503 51.8315 31.2841 48.2396 40.4635 48.2396C52.6362 48.2396 61.4664 54.592 66.9541 67.2968L46.7993 74.6803C46.5 72.2524 45.8016 70.34 44.704 68.9431C43.6397 67.513 42.2928 66.7979 40.6631 66.7979C34.9426 66.7979 32.0823 74.6636 32.0823 90.395C32.0823 97.08 32.7142 101.936 33.9781 104.962C35.2419 107.989 37.2541 109.502 40.0145 109.502C41.1121 109.502 42.1098 109.236 43.0078 108.704C43.9058 108.172 44.6541 107.424 45.2528 106.459C45.8847 105.495 46.367 104.33 46.6995 102.967C47.0654 101.57 47.2483 100.023 47.2483 98.3272H39.5655V82.1136ZM134.153 88.3995C134.153 100.14 131.476 109.685 126.121 117.035C120.8 124.352 113.866 128.011 105.318 128.011C97.7351 128.011 91.233 125.2 85.8118 119.58C78.9938 112.462 75.5848 101.786 75.5848 87.5514C75.5848 73.9153 79.1435 63.4887 86.2608 56.2715C91.549 50.9169 97.8182 48.2396 105.069 48.2396C113.949 48.2396 121.016 51.8648 126.271 59.1152C131.526 66.3656 134.153 76.127 134.153 88.3995ZM112.552 88.5492C112.552 74.6138 110.091 67.646 105.168 67.646C102.707 67.646 100.745 69.4753 99.2816 73.1337C97.8515 76.5261 97.1364 81.5149 97.1364 88.1002C97.1364 94.5856 97.8016 99.6243 99.132 103.216C100.496 106.775 102.375 108.554 104.769 108.554C107.23 108.554 109.126 106.792 110.457 103.266C111.853 99.7407 112.552 94.8351 112.552 88.5492ZM179.502 126.664H143.083V49.6364H163.887V108.155H179.502V126.664ZM206.491 126.664H185.688V49.6364H222.455V67.7458H206.491V78.9706H221.358V96.3816H206.491V126.664Z"
            fill="white"
          />
          <path
            d="M53.4648 195.358H100.961V200.58C100.961 224.845 97.3428 242.309 90.1069 252.973C82.2181 264.616 70.5481 270.437 55.097 270.437C39.9179 270.437 28.0031 264.724 19.3527 253.299C10.7566 241.765 6.45859 225.933 6.45859 205.803C6.45859 185.401 10.7838 169.325 19.4343 157.573C28.0847 145.821 39.9179 139.946 54.9338 139.946C74.8462 139.946 89.2908 150.337 98.2677 171.12L65.298 183.198C64.8084 179.226 63.6659 176.098 61.8705 173.813C60.1295 171.474 57.9261 170.304 55.2602 170.304C45.9025 170.304 41.2236 183.171 41.2236 208.904C41.2236 219.84 42.2573 227.783 44.3247 232.734C46.3921 237.685 49.6837 240.16 54.1993 240.16C55.9947 240.16 57.6269 239.725 59.0958 238.855C60.5648 237.984 61.7889 236.76 62.7682 235.182C63.8019 233.604 64.5907 231.7 65.1348 229.47C65.7333 227.185 66.0325 224.655 66.0325 221.88H53.4648V195.358ZM157.682 268.234H123.651V172.426H104.881V142.231H177.186V172.426H157.682V268.234ZM216.688 268.234H182.657V142.231H216.688V268.234Z"
            fill="white"
          />
          <path
            d="M60.5751 355.01H41.7851L40.4336 346.465H28.1831L26.7008 355.01H7.91085L24.085 287.697H46.3191L60.5751 355.01ZM38.2974 332.034L37.1203 324.187C37.0912 324.158 37.0331 323.766 36.9459 323.01C36.8878 322.371 36.5826 319.842 36.0304 315.424C35.9723 314.93 35.8996 314.262 35.8124 313.419C35.7252 312.547 35.609 311.457 35.4637 310.149L34.8969 303.784C34.8388 304.394 34.7516 305.077 34.6353 305.833C34.5481 306.589 34.4464 307.417 34.3301 308.318C34.1267 310.12 33.9814 311.37 33.8942 312.067C33.807 312.765 33.7489 313.186 33.7198 313.332L32.4555 321.484L30.6245 332.034H38.2974ZM65.8938 355.01V287.697H83.2451L92.3567 311.326C92.8508 312.721 93.403 314.32 94.0134 316.122C94.6528 317.924 95.3503 320.002 96.106 322.356L97.9806 328.285C97.3121 322.443 96.8035 317.444 96.4547 313.288C96.106 309.132 95.9316 305.644 95.9316 302.825V287.697H113.283V355.01H95.9316L86.7764 330.247C85.8173 327.602 84.9453 325.132 84.1606 322.836C83.4049 320.511 82.7365 318.302 82.1552 316.209C82.533 320.946 82.8091 325.074 82.9835 328.59C83.1579 332.107 83.2451 335.188 83.2451 337.833V355.01H65.8938ZM140.923 355.01H122.743V287.697H140.923V355.01ZM179.636 355.01H161.108L145.675 287.697H164.77L168.083 306.923C169.333 314.276 170.336 322.501 171.091 331.598C171.411 327.559 171.804 323.78 172.269 320.263C172.734 316.747 173.286 313.317 173.925 309.975L178.241 287.697H197.99L179.636 355.01ZM202.699 355.01V287.697H236.486V303.523H220.878V313.332H234.873V328.547H220.878V338.835H236.486V355.01H202.699ZM292.158 355.01H272.496L263.515 331.032C263.428 330.625 263.268 330.087 263.036 329.419C262.832 328.75 262.571 327.951 262.251 327.021L262.382 331.468V355.01H244.289V287.697H262.905C271.101 287.697 277.234 289.31 281.303 292.536C286.476 296.663 289.063 302.534 289.063 310.149C289.063 318.287 285.59 323.911 278.643 327.021L292.158 355.01ZM262.469 318.868H263.69C265.782 318.868 267.526 318.069 268.921 316.471C270.316 314.872 271.014 312.838 271.014 310.367C271.014 305.339 268.384 302.825 263.123 302.825H262.469V318.868ZM296.736 353.353V332.209C300.311 337.092 304.176 339.533 308.332 339.533C309.902 339.533 311.181 339.126 312.169 338.312C313.157 337.469 313.651 336.423 313.651 335.173C313.651 333.371 312.576 331.613 310.425 329.898L307.46 327.588C305.484 326.105 303.798 324.638 302.403 323.184C301.008 321.702 299.875 320.191 299.003 318.65C298.131 317.081 297.491 315.468 297.084 313.811C296.678 312.155 296.474 310.411 296.474 308.58C296.474 305.441 297.201 302.157 298.654 298.727C299.381 297.128 300.223 295.675 301.183 294.367C302.142 293.03 303.246 291.853 304.496 290.836C307.925 287.93 312.067 286.476 316.921 286.476C321.251 286.476 325.611 287.624 330 289.921V310.498C328.517 308.202 326.832 306.4 324.942 305.092C323.053 303.755 321.193 303.087 319.362 303.087C317.967 303.087 316.819 303.493 315.918 304.307C314.988 305.092 314.523 306.037 314.523 307.141C314.523 308.943 315.758 310.73 318.229 312.503L321.106 314.64C324.942 317.401 327.689 320.234 329.346 323.141C331.031 326.047 331.874 329.448 331.874 333.342C331.874 339.969 329.854 345.433 325.814 349.735C321.774 354.036 316.659 356.187 310.468 356.187C306.196 356.187 301.618 355.242 296.736 353.353ZM386.936 355.01H368.146L366.795 346.465H354.544L353.062 355.01H334.272L350.446 287.697H372.68L386.936 355.01ZM364.659 332.034L363.481 324.187C363.452 324.158 363.394 323.766 363.307 323.01C363.249 322.371 362.944 319.842 362.392 315.424C362.333 314.93 362.261 314.262 362.174 313.419C362.086 312.547 361.97 311.457 361.825 310.149L361.258 303.784C361.2 304.394 361.113 305.077 360.996 305.833C360.909 306.589 360.808 307.417 360.691 308.318C360.488 310.12 360.343 311.37 360.255 312.067C360.168 312.765 360.11 313.186 360.081 313.332L358.817 321.484L356.986 332.034H364.659ZM440.124 355.01H420.462L411.481 331.032C411.394 330.625 411.234 330.087 411.001 329.419C410.798 328.75 410.536 327.951 410.217 327.021L410.347 331.468V355.01H392.255V287.697H410.871C419.067 287.697 425.199 289.31 429.268 292.536C434.442 296.663 437.028 302.534 437.028 310.149C437.028 318.287 433.555 323.911 426.609 327.021L440.124 355.01ZM410.435 318.868H411.655C413.748 318.868 415.492 318.069 416.887 316.471C418.282 314.872 418.979 312.838 418.979 310.367C418.979 305.339 416.349 302.825 411.089 302.825H410.435V318.868ZM464.32 355.01H446.14V287.697H464.32V355.01ZM523.262 321.571C523.262 331.831 520.922 340.172 516.243 346.596C511.592 352.99 505.532 356.187 498.063 356.187C491.436 356.187 485.754 353.731 481.017 348.819C475.059 342.599 472.08 333.27 472.08 320.83C472.08 308.914 475.19 299.802 481.409 293.495C486.03 288.816 491.509 286.476 497.845 286.476C505.605 286.476 511.781 289.644 516.373 295.98C520.966 302.316 523.262 310.847 523.262 321.571ZM504.384 321.702C504.384 309.524 502.234 303.435 497.932 303.435C495.781 303.435 494.067 305.034 492.788 308.231C491.538 311.195 490.913 315.555 490.913 321.31C490.913 326.977 491.494 331.381 492.657 334.519C493.849 337.629 495.491 339.184 497.583 339.184C499.734 339.184 501.391 337.644 502.553 334.563C503.774 331.482 504.384 327.195 504.384 321.702Z"
            fill="#0C0C0C"
          />
          <path
            d="M240.649 254.229L247.2 205.293C263.691 214.831 278.443 219.6 291.457 219.6C299.925 219.6 306.788 217.46 312.047 213.182C317.395 208.903 320.069 203.332 320.069 196.469C320.069 189.16 317.172 183.499 311.379 179.488C305.585 175.388 297.429 173.338 286.911 173.338C275.412 173.338 263.334 175.745 250.677 180.558L266.721 60.2238H369.406V108.759H310.576L306.164 131.889C309.997 131.622 313.117 131.488 315.523 131.488C332.459 131.488 346.766 137.639 358.443 149.94C370.03 162.062 375.824 177.215 375.824 195.399C375.824 216.792 368.337 234.619 353.362 248.881C338.565 263.143 319.891 270.274 297.34 270.274C278.086 270.274 259.189 264.925 240.649 254.229ZM526.862 164.781C526.862 197.048 520.444 222.719 507.609 241.795C494.773 260.781 477.525 270.274 455.865 270.274C432.422 270.274 414.06 260.691 400.779 241.527C387.587 222.452 380.991 195.8 380.991 161.572C380.991 129.215 387.587 103.633 400.779 84.8254C414.06 65.9285 431.976 56.4801 454.528 56.4801C477.258 56.4801 494.996 66.0622 507.742 85.2265C520.489 104.391 526.862 130.909 526.862 164.781ZM471.776 164.38C471.776 124.536 465.759 104.614 453.726 104.614C441.781 104.614 435.809 122.441 435.809 158.096C435.809 200.703 441.648 222.006 453.325 222.006C459.653 222.006 464.288 217.371 467.23 208.101C470.261 198.742 471.776 184.168 471.776 164.38Z"
            fill="#0C0C0C"
          />
        </Logo>
      </LogoContainer>

      <CarCoverContainer>
        <CarCover
          ref={carCoverRef}
          src="https://assets.volkswagen.com/is/image/volkswagenag/golf-gti-tapado?Zml0PWNyb3AlMkMxJndpZD0xOTAyJmhlaT0xMjY4JmZtdD1wbmctYWxwaGEmYmZjPW9mZiY2NTE5"
          alt="Coche tapado"
        />

        <Car
          ref={carRef}
          src="https://assets.volkswagen.com/is/image/volkswagenag/golf-gti-destapado?Zml0PWNyb3AlMkMxJndpZD0xOTAyJmhlaT0xMjY4JmZtdD1wbmctYWxwaGEmYmZjPW9mZiY2NTE5"
          alt="Coche destapado"
        />
      </CarCoverContainer>

      <FinalVideo
        ref={finalVideoRef}
        src="https://assets.volkswagen.com/is/content/volkswagenag/video-gti1-0x720-3000k"
        autoPlay
        muted
        loop
        playsInline
      />

      <ScrollHintArrow pinId="gti50-reveal-pin" />
    </Section>
  );
}
